"use client"

import { Head, router, useForm } from "@inertiajs/react"

import { Breadcrumbs } from "@/components/ui/breadcrumbs"
import PublicLayout from "@/layouts/public-layout"
import type { Playlist, Tag } from "@/types"
import { ContentBodyTypeEnum } from "@/types"

import TrackForm from "./_components/track-form"
import { contentBodyAttributes } from "./tracks.constant"

interface NewTrackProps {
  playlist: Playlist
  availableTags: Tag[]
  content_type: ContentBodyTypeEnum
}

/**
 * New Track component.
 * @param playlist - The playlist.
 * @param content_type - The content type.
 * @returns The New Track component.
 */
export default function NewTrack({
  playlist,
  availableTags,
  content_type = ContentBodyTypeEnum.video,
}: NewTrackProps) {
  const { data, setData, post, processing, errors } = useForm({
    track: {
      notes: "",
      tag_ids: [] as number[],
      post_attributes: {
        title: "",
        creator: "",
        canonical_url: "",
        thumbnail_url: "",
        content_body_type: content_type,
        content_body_attributes: contentBodyAttributes[content_type],
        published: true,
      },
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const contentBodyAttrs = data.track.post_attributes
      .content_body_attributes as { image?: unknown } | undefined
    const hasImageFile = contentBodyAttrs?.image instanceof File
    post(`/playlists/${playlist.slug ?? playlist.id}/tracks`, {
      forceFormData: hasImageFile,
    })
  }

  return (
    <PublicLayout headerVariant="light">
      <Head title="New Track" />
      <section className="bg-secondary mb-3 pt-6 pb-8">
        <div className="max-w-container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <div className="mb-3">
            <Breadcrumbs maxVisibleItems={4} />
          </div>

          {/* Page header */}
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              Add Track
            </h1>
            <p className="text-md text-tertiary">
              Add content that helped you, along with your perspective on why
              and how it helped.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-container mx-auto mt-6 mb-8 flex min-h-[calc(80vh)] flex-1 flex-col gap-5 px-4 lg:px-8">
        <TrackForm
          data={data}
          setData={setData}
          errors={errors}
          availableTags={availableTags}
          processing={processing}
          submitLabel="Create Track"
          onSubmit={submit}
          onCancel={() =>
            router.visit(`/playlists/${playlist.slug ?? playlist.id}`)
          }
        />
      </main>
    </PublicLayout>
  )
}
